import { useState, type CSSProperties } from 'react';
import { useNavigate } from 'react-router-dom';
import { Drawer, DrawerBadge, Modal } from '@helios/ui/overlay';
import { Button } from '@helios/ui/button';
import { useAuth } from '@helios/auth-firebase-web';
import {
  canDeletePurchaseOrder,
  qDeliveryOrdersByPurchaseOrder,
  qPurchaseOrderForReceive,
  qPurchaseOrderLinesByOrder,
  type DeliveryOrderRow,
  type PurchaseOrderLineRow,
  type PurchaseOrderRow,
} from '@helios/data-access';
import { usePowerSyncDb, usePowerSyncQuery } from '../../adapters/powersync';
import { useFormatMoney } from '../../hooks/useFormatMoney';
import { NavIcon } from '../../shell/shared';
import { SendPurchaseOrderEmailModal } from './SendPurchaseOrderEmailModal';

function formatNumber(value: number | null): string {
  if (value == null) return '—';
  return value.toLocaleString('en-US');
}

function statusBadgeVariant(status: string | null): 'warning' | 'neutral' {
  return status === 'draft' ? 'warning' : 'neutral';
}

function statusLabel(status: string | null): string {
  if (!status) return '—';
  return status.charAt(0).toUpperCase() + status.slice(1);
}

export function PurchaseOrderDetailsDrawer({
  open,
  onOpenChange,
  purchaseOrderId,
  brandTokens,
  onReceiveOverride,
  onPrint,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  purchaseOrderId: string | null;
  brandTokens: Record<string, string>;
  /**
   * When provided, Receive in the footer calls this instead of navigating to
   * the standalone receive page. Used by the Delivery Orders receive-PO drawer
   * so the user stays inside the drawer flow.
   */
  onReceiveOverride?: (purchaseOrderId: string) => void;
  /** PRD.239 — when provided, the footer shows a Print button that emits the
   * PO id. The parent owns the print mount + the request state. */
  onPrint?: (purchaseOrderId: string) => void;
}) {
  const navigate = useNavigate();
  const { db } = usePowerSyncDb();
  const { user } = useAuth();

  const { data: headerRows = [] } = usePowerSyncQuery<PurchaseOrderRow>(
    qPurchaseOrderForReceive(purchaseOrderId ?? ''),
  );
  const { data: lines = [] } = usePowerSyncQuery<PurchaseOrderLineRow>(
    qPurchaseOrderLinesByOrder(purchaseOrderId ?? ''),
  );
  const { data: deliveries = [] } = usePowerSyncQuery<DeliveryOrderRow>(
    qDeliveryOrdersByPurchaseOrder(purchaseOrderId ?? ''),
  );

  const po = headerRows[0] ?? null;
  const hasReceived = deliveries.some((d) => d.status === 'received');
  const canReceive = po != null && !hasReceived && po.status !== 'cancelled';
  // Delete is offered for any non-cancelled PO; the modal enforces the
  // linked-deliveries guard on confirm.
  const canDelete = po != null && po.status !== 'cancelled';

  // PRD.241 — Send Email opens the review modal that posts to psendEmail.
  // No DB writes; no PO mutation; no stock movement.
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);

  const handleReceive = () => {
    if (!po) return;
    onOpenChange(false);
    if (onReceiveOverride) {
      onReceiveOverride(po.id);
    } else {
      navigate(`/inventory/purchase-orders/${po.id}/receive`);
    }
  };

  const title = po?.po_number ?? 'Purchase Order';

  return (
    <Drawer
      open={open}
      onOpenChange={onOpenChange}
      title={title}
      portalStyle={brandTokens as CSSProperties}
      width="min(720px, 60vw)"
      badges={
        po ? (
          <DrawerBadge label={statusLabel(po.status)} variant={statusBadgeVariant(po.status)} />
        ) : null
      }
      footer={
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-2)' }}>
            <Button variant="outline" size="lg" onClick={() => onOpenChange(false)}>
              Close
            </Button>
            {/* PRD.239 — Print PO. Parent owns the actual print mount. */}
            {onPrint && (
              <IconButton
                ariaLabel="Print Purchase Order"
                title="Print Purchase Order"
                onClick={() => po && onPrint(po.id)}
                disabled={!po}
              >
                <PrinterIconSvg />
              </IconButton>
            )}
            <IconButton
              ariaLabel="Send PO via email"
              title="Send via email"
              onClick={() => setEmailModalOpen(true)}
              disabled={!po}
            >
              <NavIcon name="mail" />
            </IconButton>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-2)' }}>
            {canDelete && (
              <Button
                variant="outline"
                size="lg"
                onClick={() => setDeleteModalOpen(true)}
                style={{ borderColor: '#fecaca', color: '#b91c1c' }}
              >
                Delete
              </Button>
            )}
            {canReceive && (
              <Button variant="gradient" size="lg" onClick={handleReceive}>
                Receive
              </Button>
            )}
          </div>
        </div>
      }
    >
      {/* PRD.241 — Real Send Email modal (replaces the v1 placeholder).
          Loads its own PO data via PowerSync and posts to psendEmail; no
          DB writes, no PO mutation, no stock movement. */}
      <SendPurchaseOrderEmailModal
        open={emailModalOpen}
        onOpenChange={setEmailModalOpen}
        purchaseOrderId={po?.id ?? null}
        portalStyle={brandTokens as CSSProperties}
      />
      {po && (
        <DeletePurchaseOrderModal
          open={deleteModalOpen}
          onOpenChange={setDeleteModalOpen}
          purchaseOrder={po}
          deliveries={deliveries}
          db={db}
          userName={user?.displayName ?? user?.email ?? ''}
          portalStyle={brandTokens as CSSProperties}
          onDeleted={() => {
            setDeleteModalOpen(false);
            onOpenChange(false);
          }}
        />
      )}
      {!po ? (
        <div
          style={{
            padding: 'var(--sp-6)',
            color: 'var(--text-muted)',
            fontSize: 'var(--font-size-sm)',
          }}
        >
          Loading…
        </div>
      ) : (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--sp-6)',
            padding: 'var(--sp-5) var(--sp-6)',
          }}
        >
          <HeaderInfo po={po} />
          <LinesSection lines={lines} amountsAre={po.amounts_are} />
          <DeliveriesSection deliveries={deliveries} />
          <TotalsSection total={po.total_amount} amountsAre={po.amounts_are} />
        </div>
      )}
    </Drawer>
  );
}

/**
 * Delete (soft-cancel) a Purchase Order. A PO carries no stock, so this just
 * marks it cancelled — but only once every linked Delivery Order is itself
 * cancelled. Active (draft/received) deliveries must be deleted/reversed first
 * so no received stock is left orphaned.
 */
function DeletePurchaseOrderModal({
  open,
  onOpenChange,
  purchaseOrder,
  deliveries,
  db,
  userName,
  portalStyle,
  onDeleted,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  purchaseOrder: PurchaseOrderRow;
  deliveries: DeliveryOrderRow[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  db: any;
  userName: string;
  portalStyle: CSSProperties;
  onDeleted: () => void;
}) {
  const [error, setError] = useState<string | null>(null);
  const [working, setWorking] = useState(false);

  const poLabel = purchaseOrder.po_number ?? purchaseOrder.id.slice(0, 8);
  const guard = canDeletePurchaseOrder({
    status: purchaseOrder.status,
    deliveries: deliveries.map((d) => ({ status: d.status })),
  });

  async function handleConfirm() {
    setError(null);
    if (!db) return setError('Local database not ready.');

    setWorking(true);
    try {
      // Re-read the linked deliveries so a stale drawer can't delete a PO whose
      // delivery was received in another tab since the drawer opened.
      const rows = (await db.getAll(
        `SELECT status FROM delivery_orders WHERE purchase_order_id = ?`,
        [purchaseOrder.id],
      )) as { status: string | null }[];
      const check = canDeletePurchaseOrder({ status: purchaseOrder.status, deliveries: rows });
      if (!check.ok) {
        setWorking(false);
        return setError(check.error ?? 'This purchase order cannot be deleted.');
      }

      const now = new Date().toISOString();
      await db.execute(
        `UPDATE purchase_orders SET status = 'cancelled', status_executor = ?, updated_at = ?
         WHERE id = ?`,
        [userName || null, now, purchaseOrder.id],
      );
      onDeleted();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Delete failed. Please try again.';
      setError(msg);
      setWorking(false);
    }
  }

  return (
    <Modal
      open={open}
      onOpenChange={(v) => !v && onOpenChange(false)}
      title="Delete Purchase Order"
      maxWidth={520}
      portalStyle={portalStyle}
      footer={
        <div style={{ display: 'flex', gap: 'var(--sp-3)', marginLeft: 'auto' }}>
          <Button variant="outline" size="md" onClick={() => onOpenChange(false)} disabled={working}>
            Keep PO
          </Button>
          <Button
            variant="gradient"
            size="md"
            onClick={handleConfirm}
            disabled={working || !guard.ok}
            style={{ background: '#dc2626', opacity: working || !guard.ok ? 0.5 : 1 }}
          >
            {working ? 'Deleting…' : 'Delete PO'}
          </Button>
        </div>
      }
    >
      <div
        style={{
          padding: 'var(--sp-4) var(--sp-5) var(--sp-5)',
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--sp-4)',
        }}
      >
        <p style={{ margin: 0, fontSize: 'var(--font-size-sm)', color: 'var(--text-muted)' }}>
          This marks <strong>{poLabel}</strong> as cancelled. A purchase order holds no stock, so
          nothing is reversed.
        </p>
        {!guard.ok && (
          <div
            role="alert"
            style={{
              background: '#fef3c7',
              color: '#92400e',
              border: '1px solid #fde68a',
              borderRadius: 'var(--radius-md)',
              padding: 'var(--sp-3) var(--sp-4)',
              fontSize: 'var(--font-size-sm)',
            }}
          >
            {guard.error}
          </div>
        )}
        {error && (
          <div
            role="alert"
            style={{
              background: '#fee2e2',
              color: '#991b1b',
              border: '1px solid #fecaca',
              borderRadius: 'var(--radius-md)',
              padding: 'var(--sp-3) var(--sp-4)',
              fontSize: 'var(--font-size-sm)',
              whiteSpace: 'pre-wrap',
            }}
          >
            {error}
          </div>
        )}
      </div>
    </Modal>
  );
}

function HeaderInfo({ po }: { po: PurchaseOrderRow }) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: 'var(--sp-4)',
        padding: 'var(--sp-4)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-md)',
        background: 'var(--bg-surface-2)',
      }}
    >
      <Field label="PO Date" value={po.po_date ?? '—'} />
      <Field label="Supplier" value={po.supplier_name ?? '—'} />
      <Field
        label="Amounts"
        value={po.amounts_are === 'tax_inclusive' ? 'Tax inclusive' : 'Tax exclusive'}
      />
      <Field label="Created" value={po.created_at ? po.created_at.slice(0, 10) : '—'} />
      {po.notes && (
        <div style={{ gridColumn: '1 / -1' }}>
          <Field label="Notes" value={po.notes} multiline />
        </div>
      )}
    </div>
  );
}

function PrinterIconSvg() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <polyline points="6 9 6 2 18 2 18 9" />
      <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" />
      <rect x="6" y="14" width="12" height="8" />
    </svg>
  );
}

function IconButton({
  ariaLabel,
  title,
  onClick,
  disabled = false,
  children,
}: {
  ariaLabel: string;
  title: string;
  onClick: () => void;
  disabled?: boolean;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={ariaLabel}
      title={title}
      onClick={onClick}
      disabled={disabled}
      style={{
        width: 40,
        height: 40,
        borderRadius: 'var(--radius-md)',
        border: '1px solid var(--border)',
        background: 'var(--bg-surface)',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: disabled ? 'var(--text-muted)' : 'var(--text)',
        cursor: disabled ? 'not-allowed' : 'pointer',
        padding: 0,
        opacity: disabled ? 0.5 : 1,
      }}
    >
      {children}
    </button>
  );
}

function Field({
  label,
  value,
  multiline = false,
}: {
  label: string;
  value: string;
  multiline?: boolean;
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <span
        style={{
          fontSize: 'var(--font-size-xs)',
          fontWeight: 'var(--font-weight-semi)',
          color: 'var(--text-muted)',
          textTransform: 'uppercase',
          letterSpacing: '0.6px',
        }}
      >
        {label}
      </span>
      <span
        style={{
          fontSize: 'var(--font-size-sm)',
          color: 'var(--text)',
          whiteSpace: multiline ? 'pre-wrap' : 'normal',
        }}
      >
        {value}
      </span>
    </div>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <span
      style={{
        fontSize: 'var(--font-size-sm)',
        fontWeight: 'var(--font-weight-semi)',
        color: 'var(--text)',
      }}
    >
      {children}
    </span>
  );
}

const LINE_COLUMNS = '1.6fr 70px 70px 110px 110px';

function LinesSection({
  lines,
  amountsAre,
}: {
  lines: PurchaseOrderLineRow[];
  amountsAre: string | null;
}) {
  return (
    <section style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-2)' }}>
      <SectionLabel>
        Line Items{' '}
        <span style={{ fontWeight: 'var(--font-weight-medium)', color: 'var(--text-muted)' }}>
          ({lines.length}){amountsAre === 'tax_inclusive' ? ' · Tax inclusive' : ''}
        </span>
      </SectionLabel>

      <div
        style={{
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius-md)',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: LINE_COLUMNS,
            gap: 'var(--sp-3)',
            padding: 'var(--sp-2) var(--sp-3)',
            background: 'var(--bg-surface-2)',
            fontSize: 'var(--font-size-xs)',
            fontWeight: 'var(--font-weight-semi)',
            color: 'var(--text-muted)',
            textTransform: 'uppercase',
            letterSpacing: '0.6px',
          }}
        >
          <span>Item</span>
          <span style={{ textAlign: 'right' }}>Qty</span>
          <span style={{ textAlign: 'right' }}>Bonus</span>
          <span style={{ textAlign: 'right' }}>Rate</span>
          <span style={{ textAlign: 'right' }}>Amount</span>
        </div>

        {lines.length === 0 ? (
          <div
            style={{
              padding: 'var(--sp-4) var(--sp-3)',
              color: 'var(--text-muted)',
              fontSize: 'var(--font-size-sm)',
              textAlign: 'center',
            }}
          >
            No line items.
          </div>
        ) : (
          lines.map((l) => <LineRow key={l.id} line={l} />)
        )}
      </div>
    </section>
  );
}

function LineRow({ line }: { line: PurchaseOrderLineRow }) {
  const fmt = useFormatMoney();
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: LINE_COLUMNS,
        gap: 'var(--sp-3)',
        alignItems: 'flex-start',
        padding: 'var(--sp-3)',
        borderTop: '1px solid var(--border)',
      }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
        <span
          style={{
            fontSize: 'var(--font-size-sm)',
            color: 'var(--text)',
            fontWeight: 'var(--font-weight-medium)',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {line.item_name_snapshot ?? '—'}
        </span>
        {(line.sku_snapshot || line.unit_snapshot) && (
          <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)' }}>
            {[line.sku_snapshot, line.unit_snapshot].filter(Boolean).join(' · ')}
          </span>
        )}
        {line.remarks && (
          <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)' }}>
            {line.remarks}
          </span>
        )}
      </div>
      <span style={numCellStyle}>{formatNumber(line.quantity_ordered)}</span>
      <span style={numCellStyle}>{formatNumber(line.bonus_quantity)}</span>
      <span style={numCellStyle}>{line.rate == null ? '—' : fmt(line.rate)}</span>
      <span style={{ ...numCellStyle, fontWeight: 'var(--font-weight-semi)' }}>
        {line.amount == null ? '—' : fmt(line.amount)}
      </span>
    </div>
  );
}

const numCellStyle: CSSProperties = {
  fontSize: 'var(--font-size-sm)',
  color: 'var(--text)',
  textAlign: 'right',
  fontVariantNumeric: 'tabular-nums',
};

function DeliveriesSection({ deliveries }: { deliveries: DeliveryOrderRow[] }) {
  if (deliveries.length === 0) {
    return (
      <section style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-2)' }}>
        <SectionLabel>Deliveries</SectionLabel>
        <div
          style={{
            padding: 'var(--sp-3)',
            border: '1px dashed var(--border)',
            borderRadius: 'var(--radius-md)',
            color: 'var(--text-muted)',
            fontSize: 'var(--font-size-sm)',
            textAlign: 'center',
          }}
        >
          No delivery orders yet.
        </div>
      </section>
    );
  }

  return (
    <section style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-2)' }}>
      <SectionLabel>
        Deliveries{' '}
        <span style={{ fontWeight: 'var(--font-weight-medium)', color: 'var(--text-muted)' }}>
          ({deliveries.length})
        </span>
      </SectionLabel>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-2)' }}>
        {deliveries.map((d) => (
          <div
            key={d.id}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: 'var(--sp-3)',
              border: '1px solid var(--border)',
              borderRadius: 'var(--radius-md)',
              gap: 'var(--sp-3)',
            }}
          >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
              <span
                style={{
                  fontSize: 'var(--font-size-sm)',
                  fontWeight: 'var(--font-weight-semi)',
                  color: 'var(--text)',
                }}
              >
                {d.do_number ?? d.id.slice(0, 8)}
              </span>
              <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)' }}>
                {d.received_date ?? d.delivery_date ?? d.date_raised ?? '—'}
              </span>
            </div>
            <DrawerBadge label={statusLabel(d.status)} variant={statusBadgeVariant(d.status)} />
          </div>
        ))}
      </div>
    </section>
  );
}

function TotalsSection({ total, amountsAre }: { total: number | null; amountsAre: string | null }) {
  const fmt = useFormatMoney();
  return (
    <section
      style={{
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'center',
        gap: 'var(--sp-3)',
        padding: 'var(--sp-3) var(--sp-4)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-md)',
        background: 'var(--bg-surface-2)',
      }}
    >
      <span
        style={{
          fontSize: 'var(--font-size-xs)',
          fontWeight: 'var(--font-weight-semi)',
          color: 'var(--text-muted)',
          textTransform: 'uppercase',
          letterSpacing: '0.6px',
        }}
      >
        Total {amountsAre === 'tax_inclusive' ? '(incl. tax)' : '(excl. tax)'}
      </span>
      <span
        style={{
          fontSize: 'var(--font-size-lg)',
          fontWeight: 'var(--font-weight-semi)',
          color: 'var(--text)',
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {total == null ? '—' : fmt(total)}
      </span>
    </section>
  );
}
